/**
 * nj-publish-gate: whether the event designer may publish, as one rule for the Publish button and the
 * publish handler (CC 0399). An event zone the browser cannot resolve has no correct offset, so its times
 * cannot be published any more than saved (CC 0391).
 */
import { isKnownTimeZone } from "@v0y4ge/jrni-ui";
import type { RequiredCheck } from "../events/creation-store";
import { t as i18nT } from "../../lib/i18n";

export interface PublishGateInput {
  allReady: boolean;
  approvalsOn: boolean;
  approvalBlocked: boolean;
  zone: string | null | undefined;
  busy: boolean;
}

export type PublishBlock = "not-ready" | "approval" | "zone-unknown" | "busy" | null;

/** Why publishing is refused right now, or null when it may go ahead. */
export function publishBlock(g: PublishGateInput): PublishBlock {
  if (g.busy) return "busy";
  if (!!g.zone && !isKnownTimeZone(g.zone)) return "zone-unknown";
  if (!g.allReady) return "not-ready";
  if (g.approvalsOn && g.approvalBlocked) return "approval";
  return null;
}

export const ZONE_NOT_RECOGNISED_DEFAULT =
  "This event's time zone isn't recognised, so its times can't be saved. Choose a time zone in the event's settings.";

/** One readiness answer for every Publish path: the designer's button and handler, and the Events list's
 *  row-menu Publish (CC 0420). `readiness` is eventReadiness(record), the same rows the designer shows. */
export function publishReadiness(readiness: RequiredCheck[], zone: string | null | undefined):
  { ok: true } | { ok: false; block: Exclude<PublishBlock, null | "busy" | "approval">; reason: string; step: string | null } {
  const block = publishBlock({ allReady: readiness.every((c) => c.done), approvalsOn: false, approvalBlocked: false, zone, busy: false });
  if (block === "zone-unknown") return { ok: false, block, reason: i18nT("journeys.newJourneys.zoneNotRecognised", ZONE_NOT_RECOGNISED_DEFAULT), step: "schedule" };
  if (block === "not-ready") {
    const first = readiness.find((c) => !c.done)!;
    const reason = first.step === "tickets"
      ? i18nT("events.publish.needsTicket", "Add a ticket type before publishing.")
      : i18nT("events.publish.needsField", "Add {field} before publishing.", { field: first.label.toLowerCase() });
    return { ok: false, block, reason, step: first.step };
  }
  return { ok: true };
}
